import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Pagination,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import SpotlightCard from '../Common/SpotlightCard';
import api from '../../services/api';

const AdminDashboard = () => {
  const [movies, setMovies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pagination, setPagination] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const navigate = useNavigate();

  useEffect(() => {
    fetchMovies();
  }, [currentPage]);

  const fetchMovies = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/movies?page=${currentPage}&limit=15`);
      setMovies(response.data.data);
      setPagination(response.data.pagination);
      setLoading(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch movies');
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this movie?')) {
      return;
    }

    try {
      await api.delete(`/movies/${id}`);
      // Refresh the current page
      fetchMovies();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete movie');
    }
  };

  const handleView = (id) => {
    navigate(`/movie/${id}`);
  };

  const handleEdit = (id) => {
    navigate(`/admin/edit/${id}`);
  };

  const handleAddMovie = () => {
    navigate('/admin/add');
  };

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '80vh',
          pt: 12,
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '90vh', pt: 14, pb: 8 }}>
      <Container maxWidth="xl">
        {/* Header Section */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 4,
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Box>
            <Typography
              variant="h3"
              fontWeight={800}
              sx={{
                background: 'linear-gradient(135deg, #ffffff 0%, #a1a1aa 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 1,
              }}
            >
              Admin Dashboard
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Manage your movie database • {pagination.total} total movies
            </Typography>
          </Box>

          <SpotlightCard
            as="button"
            className="spotlight-button"
            spotlightColor="rgba(99, 102, 241, 0.5)"
            onClick={handleAddMovie}
            style={{
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              border: '1px solid rgba(99, 102, 241, 0.5)',
              color: 'white',
              fontSize: '1rem',
              fontWeight: 600,
              padding: '12px 24px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <AddIcon />
            Add New Movie
          </SpotlightCard>
        </Box>

        {error && (
          <Alert
            severity="error"
            onClose={() => setError('')}
            sx={{
              mb: 3,
              background: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              borderRadius: 2,
            }}
          >
            {error}
          </Alert>
        )}

        {/* Table Section */}
        <SpotlightCard
          className="spotlight-card"
          spotlightColor="rgba(99, 102, 241, 0.2)"
          style={{
            padding: '0',
            overflow: 'hidden',
          }}
        >
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow
                  sx={{
                    background: 'rgba(99, 102, 241, 0.1)',
                    borderBottom: '2px solid rgba(99, 102, 241, 0.3)',
                  }}
                >
                  <TableCell sx={{ fontWeight: 700, color: 'white' }}>Title</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: 'white' }}>Director</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: 'white' }}>Rating</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: 'white' }}>Duration</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: 'white' }}>Genre</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: 'white', textAlign: 'center' }}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {movies.map((movie) => (
                  <TableRow
                    key={movie._id}
                    sx={{
                      '&:hover': {
                        background: 'rgba(255, 255, 255, 0.03)',
                      },
                      borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
                    }}
                  >
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>
                      {movie.title}
                    </TableCell>
                    <TableCell sx={{ color: 'text.secondary' }}>{movie.director}</TableCell>
                    <TableCell>
                      <Chip
                        label={movie.rating}
                        size="small"
                        sx={{
                          background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                          color: 'white',
                          fontWeight: 600,
                          border: '1px solid rgba(99, 102, 241, 0.5)',
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ color: 'text.secondary' }}>{movie.duration} min</TableCell>
                    <TableCell sx={{ color: 'text.secondary' }}>
                      {movie.genre?.slice(0, 2).join(', ')}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                        <IconButton
                          onClick={() => handleView(movie._id)}
                          size="small"
                          sx={{
                            color: '#6366f1',
                            background: 'rgba(99, 102, 241, 0.1)',
                            '&:hover': {
                              background: 'rgba(99, 102, 241, 0.2)',
                            },
                          }}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          onClick={() => handleEdit(movie._id)}
                          size="small"
                          sx={{
                            color: '#8b5cf6',
                            background: 'rgba(139, 92, 246, 0.1)',
                            '&:hover': {
                              background: 'rgba(139, 92, 246, 0.2)',
                            },
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          onClick={() => handleDelete(movie._id)}
                          size="small"
                          sx={{
                            color: '#ef4444',
                            background: 'rgba(239, 68, 68, 0.1)',
                            '&:hover': {
                              background: 'rgba(239, 68, 68, 0.2)',
                            },
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          <Box
            sx={{
              p: 3,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              borderTop: '1px solid rgba(255, 255, 255, 0.05)',
              background: 'rgba(0, 0, 0, 0.2)',
              flexWrap: 'wrap',
              gap: 2,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Showing {movies.length} of {pagination.total} movies (Page {pagination.page} of {pagination.pages})
            </Typography>
            
            {pagination.pages > 1 && (
              <Pagination
                count={pagination.pages}
                page={pagination.page}
                onChange={handlePageChange}
                color="primary"
                sx={{
                  '& .MuiPaginationItem-root': {
                    color: 'white',
                    borderColor: 'rgba(255, 255, 255, 0.2)',
                  },
                  '& .Mui-selected': {
                    background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                    borderColor: '#6366f1',
                  },
                }}
              />
            )}
          </Box>
        </SpotlightCard>
      </Container>
    </Box>
  );
};

export default AdminDashboard;
